import { useEffect, useRef } from "react";
import { AlertTriangle, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { usePurgeUnminted } from "./usePurgeUnminted";

export const PurgeUnmintedDialog = () => {
  const { open, changeOpen, pin, setPin, preview, error, busy, verify, confirm } = usePurgeUnminted();
  const cancelRef = useRef(null);
  useEffect(() => {
    // Enter verifies the PIN only; focus never jumps to the destructive button.
    if (preview) cancelRef.current?.focus();
  }, [preview]);

  return (
    <AlertDialog open={open} onOpenChange={changeOpen}>
      <AlertDialogTrigger asChild>
        <Button data-testid="admin-purge-unminted-button" variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10">
          <Trash2 className="h-4 w-4 mr-1" /> Remove Unminted
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="purge-confirm-dialog" className="w-[calc(100%-2rem)] max-w-lg max-h-[90dvh] overflow-y-auto rounded-lg bg-[hsl(222_22%_8%)] border-white/10 text-white">
        <AlertDialogHeader className="text-left">
          <div data-testid="purge-step-label" className="flex items-center gap-2 text-xs font-mono text-red-300">
            {preview ? <AlertTriangle className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
            {preview ? "STEP 2 OF 2 · FINAL CONFIRMATION" : "STEP 1 OF 2 · ADMIN PASSCODE"}
          </div>
          <AlertDialogTitle data-testid="purge-dialog-title" className="font-display text-lg">
            {preview ? "Confirm permanent removal" : "Verify admin passcode"}
          </AlertDialogTitle>
          <AlertDialogDescription data-testid="purge-dialog-description" className="text-white/65">
            {preview ? "This removes unminted templates and their store listings across the entire catalogue, not just the current page or filters. This cannot be undone."
              : "Removal requires your admin passcode and a separate final confirmation. Nothing is removed at this step."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && <p role="alert" data-testid="purge-error" className="text-sm text-red-300 break-words">{error}</p>}

        {preview ? (
          <>
            <div className="border-y border-white/10 py-4 space-y-2" data-testid="purge-review">
              <p data-testid="purge-template-count" className="text-lg font-semibold">{preview.count.toLocaleString()} unminted template{preview.count === 1 ? "" : "s"}</p>
              <p data-testid="purge-listing-count" className="text-sm text-white/65">{preview.listing_count.toLocaleString()} store listing{preview.listing_count === 1 ? "" : "s"}</p>
              <p data-testid="purge-protected-notice" className="text-sm text-emerald-300">Minted cards and templates with reserved print-edition codes are kept.</p>
              {preview.count === 0 && <p data-testid="purge-empty-notice" className="text-sm text-white/65">No eligible templates to remove.</p>}
            </div>
            <AlertDialogFooter className="gap-2 sm:space-x-0">
              <Button ref={cancelRef} type="button" data-testid="purge-final-cancel-button" variant="outline" disabled={busy} onClick={() => changeOpen(false)} className="border-white/15 bg-transparent text-white hover:bg-white/5">{preview.count ? "Cancel" : "Close"}</Button>
              <Button type="button" data-testid="confirm-purge-button" disabled={busy || !preview.count || !preview.confirmation_token} onClick={confirm} className="bg-red-500 text-white hover:bg-red-400 whitespace-normal h-auto min-h-10">
                {busy ? <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2 shrink-0" />}
                {busy ? "Removing…" : `Yes, remove ${preview.count.toLocaleString()}`}
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <form onSubmit={verify} className="space-y-4" data-testid="purge-passcode-form">
            <div className="space-y-2">
              <Label htmlFor="purge-admin-passcode" data-testid="purge-passcode-label">Admin passcode</Label>
              <Input id="purge-admin-passcode" data-testid="purge-passcode-input" type="password" inputMode="numeric" autoComplete="off" maxLength={10} value={pin} disabled={busy} onChange={(event) => setPin(event.target.value.replace(/[^0-9]/g, ""))} aria-invalid={Boolean(error)} className="bg-[hsl(220_18%_12%)] border-white/15" />
            </div>
            <AlertDialogFooter className="gap-2 sm:space-x-0">
              <Button type="button" data-testid="purge-passcode-cancel-button" variant="outline" disabled={busy} onClick={() => changeOpen(false)} className="border-white/15 bg-transparent text-white hover:bg-white/5">Cancel</Button>
              <Button type="submit" data-testid="purge-verify-passcode-button" disabled={busy || !/^[0-9]{4,10}$/.test(pin)} className="bg-cyan-400 text-black hover:bg-cyan-300">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{busy ? "Verifying…" : "Verify & review"}
              </Button>
            </AlertDialogFooter>
          </form>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};